import React from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import {
  styles,
  NumberAsTwoDigits,
  Row,
  Table,
  TextCell,
  CellTextBoxNoBorder,
} from "./reportingHelpers";
import { parseISO8601 } from "./iso8601";
import { IntlProvider } from "react-intl";
import { FixtureReportsHeader, ScoringTable } from "./FixtureReportsHeader";
import { FixtureReportsFooter } from "./FixtureReportsFooter";

let players = {};

export const PlayByPlayReportFull = (props) => {
  const { locale, intl } = props;
  const {
    teams,
    fixture,
    scoringIntervals,
    playByPlay,
    roles,
    fixtureProfile: { profile: fixtureProfile },
  } = props.data;
  let homeTeamPeriods,
    awayTeamPeriods = [];
  let periodsScores = [];

  const teamIds = Object.keys(teams);
  const homeTeamId = teams[teamIds[0]].isHome ? teamIds[0] : teamIds[1];
  const awayTeamId = teams[teamIds[0]].isHome ? teamIds[1] : teamIds[0];

  const homeTeam = { ...teams[homeTeamId], entityId: homeTeamId };
  const awayTeam = { ...teams[awayTeamId], entityId: awayTeamId };

  const periodIds = [...new Set(playByPlay.map((play) => play.periodId))];
  const periodLabel = (periodId) => {
    if (periodId < 5) {
      return "Q" + periodId.toString();
    } else {
      return "OT" + (periodId - 10).toString();
    }
  };

  if (homeTeam && awayTeam && scoringIntervals) {
    homeTeamPeriods = parseScoringIntervals(homeTeam, scoringIntervals, intl);
    awayTeamPeriods = parseScoringIntervals(awayTeam, scoringIntervals, intl);

    let lastHomeScore = 0,
      lastAwayScore = 0;
    homeTeamPeriods.forEach((period, index) => {
      const awayPeriod = awayTeamPeriods[index];
      const newHomeScore = period.interval2 || period.interval1;
      const newAwayScore = awayPeriod.interval2 || awayPeriod.interval1;
      periodsScores.push(
        `${newHomeScore - lastHomeScore}-${newAwayScore - lastAwayScore}`
      );
      lastHomeScore = newHomeScore;
      lastAwayScore = newAwayScore;
    });

    homeTeam["score"] = lastHomeScore;
    awayTeam["score"] = lastAwayScore;

    [...homeTeam.players, ...awayTeam.players].forEach((player) => {
      players[player.personId] = player;
    });

    return (
      <IntlProvider locale={locale ?? "en-US"}>
        <Document>
          {periodIds.map((period, index) => {
            const playByPlayRows = playByPlay.filter(
              (event) =>
                event.periodId === period &&
                ![
                  "fixture",
                  "period",
                  "main",
                  "possessionArrow",
                  "possession",
                ].includes(event.eventType)
            );

            return (
              <Page wrap key={index} size="A4" style={styles.page}>
                <PlayByPlayReportPage
                  periodLabel={periodLabel(period)}
                  {...{
                    homeTeam,
                    awayTeam,
                    teams,
                    playByPlayRows,
                    fixture,
                    homeTeamPeriods,
                    awayTeamPeriods,
                    periodsScores,
                    intl,
                    roles,
                    fixtureProfile,
                  }}
                />
              </Page>
            );
          })}
        </Document>
      </IntlProvider>
    );
  } else {
    return null;
  }
};

const shortenFullName = (names) =>
  names
    .split(" ")
    .reverse()
    .map((n, index) => (index ? n.charAt() : n))
    .join(" ");
const sortOptions = (options) => [
  ...(options.includes("pointsInThePaint") ? ["pointsInThePaint"] : []),
  ...(options.includes("secondChance") ? ["secondChance"] : []),
  ...(options.includes("fromTurnover") ? ["fromTurnover"] : []),
  ...(options.includes("fastBreak") ? ["fastBreak"] : []),
  ...options.filter(
    (k) =>
      ![
        "pointsInThePaint",
        "secondChance",
        "fromTurnover",
        "fastBreak",
      ].includes(k)
  ),
];

const PlayByPlayReportPage = ({
  homeTeam,
  awayTeam,
  playByPlayRows,
  fixture,
  homeTeamPeriods,
  awayTeamPeriods,
  periodsScores,
  periodLabel,
  intl,
  roles,
  fixtureProfile,
}) => {
  const rowsWithCalculatedDescriptionsAndHeights = playByPlayRows.map(
    (event) => {
      const player = players[event.personId];
      const isHomeEvent = homeTeam.entityId === event.entityId;
      const homeTeamScore = event.scores[homeTeam.entityId];
      const awayTeamScore = event.scores[awayTeam.entityId];

      const eventTypeDesc = intl.messages[
        `reports.playbyplay.${event.eventType}`
      ]
        ? intl.formatMessage({ id: `reports.playbyplay.${event.eventType}` })
        : event.eventType;
      const eventSubTypeDesc = event.subType
        ? intl.messages[`reports.playbyplay.${event.subType}`]
          ? ` ${intl.formatMessage({
              id: `reports.playbyplay.${event.subType}`,
            })}`
          : ` ${event.subType}`
        : "";
      const eventSuccessDesc =
        event.success === true
          ? ` ${intl.messages["reports.playbyplay.made"]}`
          : event.success === false
          ? ` ${intl.messages["reports.playbyplay.missed"]}`
          : "";
      const eventOptionsDesc =
        event.options && Object.keys(event.options).length
          ? ` ${sortOptions(
              Object.keys(event.options).filter(
                (k) =>
                  event.options[k] === true ||
                  typeof event.options[k] === "number"
              )
            )
              .map((k) => {
                if (typeof event.options[k] === "number") {
                  return `${intl.messages[`reports.playbyplay.${k}`] || k} - ${
                    event.options[k]
                  }`;
                }
                return intl.messages[`reports.playbyplay.${k}`] || k || "";
              })
              .join(" ")}`
          : "";
      const eventTrailingCount = event.trailingCount
        ? ` (${event.trailingCount})`
        : "";
      let description =
        eventTypeDesc +
        " " +
        eventOptionsDesc +
        eventSubTypeDesc +
        eventSuccessDesc +
        eventTrailingCount;
      if (player) {
        description =
          player.bib +
          " " +
          shortenFullName(player.personFullName) +
          " " +
          eventTypeDesc +
          " " +
          eventOptionsDesc +
          eventSubTypeDesc +
          eventSuccessDesc +
          eventTrailingCount;
      }

      if (event.eventType === "videoReview") {
        description = [
          `reports.playbyplay.${event.subType}`,
          `reports.playbyplay.videoReview.reason.${event.options.reason.toLowerCase()}`,
          `reports.playbyplay.videoReview.decision.${event.options.decision.toLowerCase()}`,
        ].map((id) => intl.formatMessage({ id })).join(" - ");
      }

      const rowHeightSingle = 4.4; // the unit is mm
      let rowHeight = 1; // relative height -  1 means single row, 2 double height etc.
      let rowHeightIncreased;
      const maxCharactersPerLine = 64; // at least this much seems to fit without overflowing the cell
      if (description.length > maxCharactersPerLine) {
        rowHeight = Math.ceil(description.length / maxCharactersPerLine);
        rowHeightIncreased = `${rowHeight * rowHeightSingle}mm`;
      }
      return {
        ...event,
        description,
        rowHeight,
        rowHeightIncreased,
        isHomeEvent,
        homeTeamScore,
        awayTeamScore,
      };
    }
  );

  return (
    <React.Fragment>
      <View style={{ marginBottom: "3mm" }}>
        <FixtureReportsHeader
          {...{
            homeTeam,
            awayTeam,
            fixture,
            roles,
            periodsScores,
            title: intl.formatMessage({ id: "reports.playbyplay" }),
            intl,
          }}
        />
      </View>
      <View style={{ ...styles.section, marginBottom: 5 }}>
        <ScoringTable
          {...{
            homeTeam,
            awayTeam,
            homeTeamPeriods,
            awayTeamPeriods,
            intl,
            fixtureProfile,
          }}
        />
      </View>
      {periodLabel === "Q1" ? (
        <View
          style={{
            marginBottom: 9 /* to align vertically rows possibly overflowing to the next page */,
            fontSize: "8pt",
          }}
        >
          <Table borderless>
            <Row style={{ width: "100%", border: 0, borderBottomWidth: 0 }}>
              <TextCell
                style={{
                  width: "100%",
                  textAlign: "left",
                  fontWeight: "bold",
                  border: 0,
                  borderBottomWidth: 0,
                }}
              >
                {intl.formatMessage({ id: "reports.playbyplay.starting5" })}
              </TextCell>
            </Row>
            <Row style={{ border: 0, borderBottomWidth: 0 }}>
              <TextCell
                flex
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  borderRightWidth: 0,
                }}
              >
                {homeTeam.name}
              </TextCell>
              {homeTeam.players
                .filter((p) => p.starter)
                .map((p) => (
                  <TextCell
                    key={p.personId}
                    flex
                    style={{ textAlign: "left", border: 0 }}
                  >{`${p.bib} ${p.personFullName}`}</TextCell>
                ))}
            </Row>
            <Row style={{ border: 0, borderBottomWidth: 0 }}>
              <TextCell
                flex
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  borderRightWidth: 0,
                }}
              >
                {awayTeam.name}
              </TextCell>
              {awayTeam.players
                .filter((p) => p.starter)
                .map((p) => (
                  <TextCell
                    key={p.personId}
                    flex
                    style={{ textAlign: "left", border: 0 }}
                  >{`${p.bib} ${p.personFullName}`}</TextCell>
                ))}
            </Row>
          </Table>
        </View>
      ) : null}
      <CellTextBoxNoBorder style={{ width: "100%" }}>
        {periodLabel}
      </CellTextBoxNoBorder>
      <Table borderless>
        {rowsWithCalculatedDescriptionsAndHeights.map((e, index, array) => {
          return (
            <React.Fragment key={index}>
              <EventsTableHeader
                intl={intl}
                rowIndex={index}
                rowHeights={array.map((a) => a.rowHeight)}
                homeTeamName={homeTeam.name}
                awayTeamName={awayTeam.name}
                periodLabel={periodLabel}
              ></EventsTableHeader>
              <Row
                style={{
                  fontSize: 7,
                  borderLeftWidth: 1,
                  border: 0,
                  ...(e.rowHeightIncreased && { height: e.rowHeightIncreased }),
                }}
              >
                <TextCell width="2cm">
                  {parseISO8601(e.clock).minutes +
                    ":" +
                    NumberAsTwoDigits({ value: parseISO8601(e.clock).seconds })}
                </TextCell>
                <TextCell left flex>
                  {e.isHomeEvent ? e.description : " "}
                </TextCell>
                <TextCell width="1.5cm">
                  {e.homeTeamScore} - {e.awayTeamScore}
                </TextCell>
                <TextCell width="1cm">
                  {e.homeTeamScore - e.awayTeamScore}
                </TextCell>
                <TextCell left flex>
                  {e.isHomeEvent ? " " : e.description}
                </TextCell>
              </Row>
            </React.Fragment>
          );
        })}
      </Table>
      <FixtureReportsFooter roles={roles} intl={intl} />
    </React.Fragment>
  );
};

const EventsTableHeader = ({
  rowIndex,
  rowHeights,
  homeTeamName,
  awayTeamName,
  intl,
  periodLabel,
}) => {
  const rowsFittingOnPage = periodLabel === "Q1" ? 45 : 49; // because Q1 has starters which decrease vertical space
  // TODO a bigger number of rows could be applied after the first page break
  const heightsSoFar = rowHeights.slice(0, rowIndex).reduce((a, b) => a + b, 0);
  const breakPage =
    (heightsSoFar + rowHeights[rowIndex]) % rowsFittingOnPage <
    heightsSoFar % rowsFittingOnPage;
  if (rowIndex === 0 || breakPage) {
    return (
      <Row
        break={breakPage}
        style={{
          fontSize: 7,
          borderTopWidth: 1,
          borderLeftWidth: 1,
          border: 0,
          fontWeight: "bold",
          backgroundColor: "#f0f0f0",
        }}
      >
        <TextCell width="2cm">
          {intl.formatMessage({ id: "reports.playbyplay.gametime" })}
        </TextCell>
        <TextCell flex>{homeTeamName}</TextCell>
        <TextCell width="1.5cm">
          {intl.formatMessage({ id: "reports.playbyplay.score" })}
        </TextCell>
        <TextCell width="1cm">
          {intl.formatMessage({ id: "reports.playbyplay.diff" })}
        </TextCell>
        <TextCell flex>{awayTeamName}</TextCell>
      </Row>
    );
  } else {
    return null;
  }
};

const parseScoringIntervals = (teamData, scoringIntervals) => {
  const periods = [];

  let newPeriod = {
    interval1: 0,
    interval2: undefined,
    label: "",
    isOverTime: false,
    interval_number_start: 0,
    interval_number_end: 0,
  };
  let periodCount = 1;

  scoringIntervals.forEach((element, index) => {
    if (element.interval_number < 9) {
      if (element.interval_number % 2) {
        newPeriod.interval1 = element[teamData.entityId];
        newPeriod.interval2 = undefined;
        newPeriod.label = "Q" + periodCount.toString();
        newPeriod.isOverTime = false;
        newPeriod.interval_number_start = element.interval_number;
        newPeriod.interval_number_end = element.interval_number;
        periodCount++;

        if (index === scoringIntervals.length - 1) {
          periods.push({ ...newPeriod });
        }
      } else {
        newPeriod.interval2 = element[teamData.entityId];
        newPeriod.interval_number_end = element.interval_number;
        periods.push({ ...newPeriod });
      }
    } else {
      if (element.interval_number === 9) {
        periodCount = 1;
      }
      newPeriod.interval1 = element[teamData.entityId];
      newPeriod.interval_number_start = element.interval_number;
      newPeriod.interval_number_end = element.interval_number;
      newPeriod.label = "OT" + periodCount.toString();
      newPeriod.interval2 = undefined;
      newPeriod.isOverTime = true;
      periodCount++;
      periods.push({ ...newPeriod });
    }
  });

  return periods;
};
